import React, { useState } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { Login } from './pages/Login/Login'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  CssBaseline,
  StyledEngineProvider,
  Theme,
  ThemeProvider,
  unstable_createMuiStrictModeTheme as createMuiTheme,
} from '@mui/material'
import { blue, deepOrange, orange, pink } from '@mui/material/colors'
import { ThemeContext } from './context/ThemeContext'
import { GlobalError } from './context/GlobalError'
import { GlobalErrorNotifications } from './components/GlobalErrorNotification/GlobalErrorNotifications'
import { AuthProvider } from './context/Auth'
import { UserIndex } from './pages/UserIndex/UserIndex'
import { UserProfile } from './pages/UserProfile/UserProfile'
import { marked } from 'marked'
import { GameList } from './pages/GameList/GameList'
import { Game } from './pages/Game/Game'
import FaviconNotification from './faviconNotify'
import { ItemsIndex } from './pages/ItemsIndex/ItemsIndex'
import { SpellsIndex } from './pages/SpellsIndex/SpellsIndex'

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

document.addEventListener('visibilitychange', event => {
  if (document.visibilityState === 'visible') {
    FaviconNotification?.remove()
  }
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
})

marked.setOptions({
  renderer: new marked.Renderer(),
  pedantic: false,
  gfm: true,
  breaks: false,
})

function App() {
  if (!localStorage.theme) {
    localStorage.theme = 'dark'
  }
  const [darkState, setDarkState] = useState(localStorage.theme === 'dark')
  const toggleTheme = () => {
    localStorage.theme = localStorage?.theme === 'dark' ? 'light' : 'dark'
    setDarkState(localStorage.theme === 'dark')
  }
  const primaryTextColor = darkState ? '#dddddd' : '#000000'
  const theme = createMuiTheme({
    palette: {
      mode: darkState ? 'dark' : 'light',
      text: {
        primary: primaryTextColor,
      },
      primary: {
        main: darkState ? orange[500] : blue[600],
      },
      secondary: {
        main: darkState ? deepOrange[900] : pink['A400'],
      },
      background: {
        default: darkState ? '#303030' : '#fff',
        paper: darkState ? '#424242' : '#fff',
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'unset' } },
      },
      MuiCssBaseline: {
        styleOverrides: {
          textarea: {
            color: darkState ? 'white' : 'black',
            background: darkState ? '#6c6c6c' : 'white',
          },
          'textarea:focus': {
            outline: 'none',
          },
          blockquote: {
            paddingLeft: '12px',
            borderLeft: '2px solid',
            borderColor: darkState ? '#b9b8b8' : '#9b9b9b',
            color: darkState ? '#9f9f9f' : '#6c6c6c',
          },
          'a:visited': {
            color: darkState ? orange[800] : blue[300],
          },
          'a:link': {
            color: darkState ? orange[500] : blue[800],
          },
          pre: {
            background: darkState ? '#2d3136' : '#efefef',
            borderColor: darkState ? '#52555a' : '#d3d3d3',
            borderStyle: 'solid',
            borderWidth: '1px',
            borderRadius: '5px',
            fontSize: '0.9em',
            padding: '4px',
            code: {
              color: primaryTextColor,
              border: 'none',
              background: 'none',
              fontSize: '1em',
              padding: 0,
            },
          },
          code: {
            color: darkState ? '#d5862c' : '#e12b88',
            background: darkState ? '#2d3136' : '#efefef',
            borderColor: darkState ? '#52555a' : '#d3d3d3',
            borderStyle: 'solid',
            borderWidth: '1px',
            borderRadius: '5px',
            fontSize: '0.9em',
            padding: '2px',
          },
          body: {
            scrollbarColor: '#6b6b6b',
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              backgroundColor: '#424242',
              width: '0.6em',
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: 0,
              backgroundColor: '#6b6b6b',
              minHeight: 24,
              border: '1px solid #2b2b2b',
            },
            '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
              backgroundColor: '#959595',
            },
            '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
              backgroundColor: '#959595',
            },
            '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#959595',
            },
            '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
              backgroundColor: '#424242',
            },
          },
        },
      },
    },
  })

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <GlobalError>
          <ThemeContext.Provider value={{ toggleTheme, darkState }}>
            <QueryClientProvider client={queryClient}>
              <CssBaseline />
              <BrowserRouter>
                <AuthProvider>
                  <Routes>
                    <Route path="/" element={<GameList />} />
                    <Route path="/users" element={<UserIndex />} />
                    <Route path="/items" element={<ItemsIndex />} />
                    <Route path="/spells" element={<SpellsIndex />} />
                    <Route path="/users/:user_id" element={<UserProfile />} />
                    <Route path="/games/:game_slug" element={<Game />} />
                    <Route path="/login" element={<Login />} />
                  </Routes>
                </AuthProvider>
              </BrowserRouter>
              <GlobalErrorNotifications />
            </QueryClientProvider>
          </ThemeContext.Provider>
        </GlobalError>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

export default App
